import { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { withTimeout } from "./api";

// Mirrors the rest of the app's one-time-fetch-on-auth-change pattern
// (see FanAccount/artists_table's onAuthStateChanged + getDoc) rather than
// an onSnapshot subscription, since nothing else in the codebase uses
// live listeners for user data.
//
// Fetches the whole artistRankings subcollection - active and archived
// (unsaved-then-remembered) docs alike - and sorts/splits client-side
// rather than relying on a Firestore orderBy, since an archived doc's
// position field is stale the moment it's archived (see rankingUtils.js's
// archiveRanking) and would otherwise interleave oddly with active ones.
export default function useArtistRankings(uid) {
  const [allDocs, setAllDocs] = useState([]);
  const [loading, setLoading] = useState(!!uid);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!uid) {
      setAllDocs([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const snap = await withTimeout(getDocs(collection(db, "users", uid, "artistRankings")));
      setAllDocs(snap.docs.map((d) => d.data()));
    } catch (err) {
      console.error("Error fetching artist rankings:", err);
      setError(err?.message || "Couldn't load your rankings.");
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const rankings = useMemo(
    () =>
      allDocs
        .filter((r) => r.active !== false)
        .sort((a, b) => a.position - b.position),
    [allDocs]
  );
  const archivedByKey = useMemo(
    () => new Map(allDocs.filter((r) => r.active === false).map((r) => [r.artistKey, r])),
    [allDocs]
  );
  const rankedKeys = useMemo(() => new Set(rankings.map((r) => r.artistKey)), [rankings]);

  return { rankings, rankedKeys, archivedByKey, loading, error, refetch };
}
